import type { RecordKind, SearchHit, SessionMembership } from '@contracts';
import { useQueryClient } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import { createContext, type ReactNode, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { Link, Navigate, Outlet, useParams } from 'react-router';
import { advanceEpoch } from '../lib/api';
import { takeReturnPath, useSession } from '../session/session';
import { AuthFrame } from '../session/SignedOut';
import { AppShell } from '../shell/AppShell';
import { buttonClass } from '../ui/Button';

export interface Recent { kind: RecordKind; id: string; title: string; context: string; status: string | null }
interface ScopeValue {
  mspId: string; mspName: string; memberships: SessionMembership[]; coverage: string; role: string;
  recents: Recent[]; remember: (hit: SearchHit) => void;
}
const ScopeContext = createContext<ScopeValue | null>(null);
export function useScope() {
  const value = useContext(ScopeContext);
  if (!value) throw new Error('useScope outside a workspace');
  return value;
}

const LAST_SCOPE = 'rmm.scope';
export const workspaceName = (m: SessionMembership) => m.mspName ?? 'Unnamed workspace';
export function coverageLabel(memberships: SessionMembership[]) {
  if (memberships.some(m => !m.organizationId)) return 'All customers';
  const parts = memberships.map(m => m.siteId
    ? `${m.organizationName ?? 'Customer'} · ${m.siteName ?? 'one site'}` : m.organizationName ?? 'One customer');
  return [...new Set(parts)].join(', ');
}
export const recordContext = (hit: Pick<SearchHit, 'kind' | 'organizationName' | 'siteName'>) =>
  hit.kind === 'organization' ? 'Customer' : hit.kind === 'site' ? hit.organizationName ?? 'Site'
    : [hit.organizationName, hit.siteName].filter(Boolean).join(' · ');

/** Distinct workspaces (MSPs) the signed-in person belongs to. */
export function useWorkspaces() {
  const { session } = useSession();
  return useMemo(() => {
    const groups = new Map<string, SessionMembership[]>();
    for (const m of session.memberships) groups.set(m.mspId, [...(groups.get(m.mspId) ?? []), m]);
    return [...groups.entries()].map(([id, ms]) => ({ id, name: workspaceName(ms[0]!), coverage: coverageLabel(ms) }));
  }, [session.memberships]);
}

export function ChooseWorkspace() {
  const workspaces = useWorkspaces();
  const back = takeReturnPath();
  let last: string | null = null;
  try { last = localStorage.getItem(LAST_SCOPE); } catch { /* storage unavailable */ }
  if (back && workspaces.some(w => back.startsWith(`/w/${w.id}/`))) return <Navigate to={back} replace />;
  const target = workspaces.find(w => w.id === last) ?? workspaces[0]!;
  return <Navigate to={`/w/${target.id}/today`} replace />;
}

function ScopeProvider({ mspId, memberships, children }: { mspId: string; memberships: SessionMembership[]; children: ReactNode }) {
  const queryClient = useQueryClient();
  const { session } = useSession();
  const [recents, setRecents] = useState<Recent[]>([]);
  // Runs before any child query starts: responses still in flight for another
  // scope are discarded, and that scope's cached records are dropped.
  useLayoutEffect(() => {
    advanceEpoch();
    queryClient.removeQueries({ predicate: q => q.queryKey[0] === 'scope' && q.queryKey[1] !== mspId });
    try { localStorage.setItem(LAST_SCOPE, mspId); } catch { /* storage unavailable */ }
    // Leaving a workspace (switch, revoked access or sign-out) drops everything loaded in it.
    return () => { advanceEpoch(); queryClient.removeQueries({ queryKey: ['scope', mspId] }); };
  }, [mspId, queryClient]);
  const remember = useCallback((hit: SearchHit) => {
    setRecents(list => [{ kind: hit.kind, id: hit.id, title: hit.title, context: recordContext(hit), status: hit.status },
      ...list.filter(r => r.id !== hit.id)].slice(0, 6));
  }, []);
  const value = useMemo<ScopeValue>(() => ({
    mspId, memberships, recents, remember,
    mspName: workspaceName(memberships[0]!), coverage: coverageLabel(memberships),
    // An owner is always MSP-wide (migration 0048); an admin is a technician who manages people (WRMM-133).
    role: memberships.some(m => m.role === 'owner') ? 'Owner' : session.managesPeopleIn.includes(mspId) ? 'Admin' : 'Technician',
  }), [mspId, memberships, recents, remember, session.managesPeopleIn]);
  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
}

export function ScopeLayout() {
  const { mspId } = useParams();
  const { session } = useSession();
  const memberships = useMemo(() => session.memberships.filter(m => m.mspId === mspId), [session.memberships, mspId]);
  if (!mspId || !memberships.length) return <WorkspaceUnavailable />;
  return (
    <ScopeProvider key={mspId} mspId={mspId} memberships={memberships}>
      <AppShell><Outlet /></AppShell>
    </ScopeProvider>
  );
}

/** A workspace page in a window of its own (the remote desktop viewer): the same scope, without the console around it. */
export function BareScopeLayout() {
  const { mspId } = useParams();
  const { session } = useSession();
  const memberships = useMemo(() => session.memberships.filter(m => m.mspId === mspId), [session.memberships, mspId]);
  if (!mspId || !memberships.length) return <WorkspaceUnavailable />;
  return <ScopeProvider key={mspId} mspId={mspId} memberships={memberships}><Outlet /></ScopeProvider>;
}

function WorkspaceUnavailable() {
  const workspaces = useWorkspaces();
  return (
    <AuthFrame>
      <div className="rounded-2xl border border-line bg-panel p-8 shadow-pop">
        <h1 className="text-xl font-semibold tracking-tight">This workspace isn’t available</h1>
        <p className="mt-2 text-ink-2">The link may belong to a workspace you’re not a member of, or your access has changed. Nothing from it was loaded.</p>
        <div className="mt-6 space-y-2">
          {workspaces.map(w => (
            <Link key={w.id} to={`/w/${w.id}/today`} className={buttonClass('secondary', 'md', 'h-auto w-full justify-start py-2.5')}>
              <Building2 className="size-4 text-ink-3" />
              <span className="text-left"><span className="block">{w.name}</span><span className="block text-[12.5px] font-normal text-ink-3">{w.coverage}</span></span>
            </Link>
          ))}
        </div>
      </div>
    </AuthFrame>
  );
}
